(() => {
  if (window.RivalVehicleMedia) return;
  const cacheKey = 'rival-vehicle-media-v1';
  const observed = new WeakMap();
  const byHandle = new Map();
  const byTitle = new Map();
  const remembered = new Map();
  const failedImages = new Set();
  const normalTitle = value => String(value || '').replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim().toLowerCase();
  const parse = value => { try { return JSON.parse(value || 'null'); } catch { return null; } };
  let queued = false;

  function loadIndex() {
    const records = parse(document.querySelector('[data-rival-vehicle-media]')?.textContent);
    for (const record of Array.isArray(records) ? records : []) {
      if (!record?.handle || !record.image) continue;
      byHandle.set(record.handle, record);
      const title = normalTitle(record.title);
      if (!byTitle.has(title)) byTitle.set(title, record);
      else if (byTitle.get(title)?.handle !== record.handle) byTitle.set(title, null);
    }
    try {
      for (const [id, entry] of Object.entries(parse(localStorage.getItem(cacheKey)) || {})) {
        if (entry?.expiresAt > Date.now() && byHandle.has(entry.handle)) remembered.set(id, entry);
      }
    } catch { /* Images also work when browser storage is unavailable. */ }
  }

  function selection() { return window.RivalStorefrontFitment?.storedSelection?.() || null; }
  function vehicleKey(value) {
    return window.RivalVehiclePresentation?.garageEntry?.(value)?.id || value?.id || null;
  }
  function recordFor(value) {
    if (!value) return null;
    // A confirmed destination always wins, even when it has no approved image.
    if (value.destination?.handle) return byHandle.get(value.destination.handle) || null;
    for (const key of [vehicleKey(value), value.applicationId && `application:${value.applicationId}`]) {
      const entry = remembered.get(key);
      if (entry?.expiresAt > Date.now()) return byHandle.get(entry.handle) || null;
    }
    // Old saved manual cars can use an exact collection title. Never fuzzy-match a model or year.
    return byTitle.get(normalTitle(value.label)) || null;
  }
  function remember(value) {
    const handle = value?.destination?.handle;
    if (!handle) return;
    const expiresAt = Date.parse(value.expiresAt);
    const entry = { handle, expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + 30 * 86400000 };
    let changed = false;
    for (const key of new Set([vehicleKey(value), value.applicationId && `application:${value.applicationId}`])) {
      if (!key) continue;
      const previous = remembered.get(key);
      if (previous?.handle === handle && previous.expiresAt === entry.expiresAt) continue;
      changed = true;
      remembered.delete(key);
      if (byHandle.has(handle)) remembered.set(key, entry);
    }
    if (!changed) return;
    for (const [key, item] of remembered) if (item.expiresAt <= Date.now()) remembered.delete(key);
    while (remembered.size > 24) remembered.delete(remembered.keys().next().value);
    try { localStorage.setItem(cacheKey, JSON.stringify(Object.fromEntries(remembered))); } catch { /* Memory fallback. */ }
  }
  function savedVehicles() {
    try {
      const saved = parse(localStorage.getItem('rival-garage-v1'));
      return Array.isArray(saved?.vehicles) ? saved.vehicles : [];
    } catch { return []; }
  }
  function thumbnail(parent, record, className) {
    let image = parent.querySelector(':scope > .rival-vehicle-thumbnail');
    if (!record || failedImages.has(record.image)) {
      image?.remove();
      parent.classList.remove(className);
      return;
    }
    if (!image) {
      image = document.createElement('img');
      image.className = 'rival-vehicle-thumbnail';
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.width = 128;
      image.height = 80;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => {
        failedImages.add(image.getAttribute('src'));
        image.remove();
        parent.classList.remove(className);
      });
      parent.append(image);
    }
    if (image.getAttribute('src') !== record.image) image.setAttribute('src', record.image);
    image.title = record.credit || record.title;
    parent.classList.add(className);
  }
  function render(root) {
    const selected = selection();
    remember(selected);
    for (const identity of root.querySelectorAll('.rival-vehicle-finder__identity > .rival-vehicle-identity')) {
      thumbnail(identity, recordFor(selected), 'rival-summary-with-photo');
    }
    for (const panel of root.querySelectorAll('[data-rival-vehicle-details]')) {
      thumbnail(panel, recordFor(selected), 'rival-vehicle-details--photo');
    }
    const entries = new Map(savedVehicles().map(value => [vehicleKey(value), value]));
    for (const card of root.querySelectorAll('[data-rival-garage-list] > .rival-garage__vehicle')) {
      const key = card.querySelector('[data-rival-garage-use]')?.dataset.rivalGarageUse;
      const vehicle = key === vehicleKey(selected) ? selected : entries.get(key);
      thumbnail(card, recordFor(vehicle), 'rival-garage__vehicle--photo');
    }
  }
  function scan() {
    for (const root of document.querySelectorAll('[data-rival-vehicle-finder]')) {
      if (observed.has(root)) { observed.get(root)(); continue; }
      const refresh = () => {
        observer.disconnect();
        render(root);
        watch();
      };
      const observer = new MutationObserver(refresh);
      const watch = () => observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-open-panel'] });
      observed.set(root, refresh);
      refresh();
    }
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; scan(); });
  }
  window.RivalVehicleMedia = { scan: schedule };
  document.addEventListener('rival:selection-changed', event => { remember(event.detail || selection()); schedule(); });
  document.addEventListener('rival:finder-ready', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('storage', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { loadIndex(); schedule(); }, { once: true });
  else { loadIndex(); schedule(); }
})();
